import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { JsonLike } from '../validation-helpers/object-validation';

/**
 * Helper service to create basic CRUD services for 'simple' backend models
 */
@Injectable({
	providedIn: 'root',
	deps: [HttpClient],
})
export class ServiceHelperService {
	/**
	 * Constructor
	 * @param http
	 * @param appSettings
	 */
	constructor(private http: HttpClient) {}

	/**
	 * Creates a helper class with CRUD operations for 'default' models
	 * @param modelName
	 */
	public createHelper<T extends ServiceModel>(modelName: string): ServiceHelper<T> {
		return {
			getAll: () => this.http.get<T[]>(`${modelName}/`).pipe(shareReplay(1)),
			get: (id: number) => this.http.get<T>(`${modelName}/${id}`),
			save: (obj: T) => {
				if (obj.id == null || obj.id <= 0) {
					// new one
					return this.http.post<T>(`${modelName}/`, obj);
				} else {
					// Updating
					return this.http.put<T>(`${modelName}/${obj.id}`, obj);
				}
			},
			delete: (id: number) => this.http.delete(`${modelName}/${id}`),
		};
	}
}

export interface ServiceHelper<T extends ServiceModel> {
	/** Get all entities from the backend */
	getAll: () => Observable<T[]>;

	/** Get entity by id */
	get: (id: number) => Observable<T>;

	/** Saves the given object to the backend */
	save: (obj: T) => Observable<T>;

	/** Deletes the given object */
	delete: (id: number) => Observable<Object>;
}

/**
 * Model to be consumed by the service
 */
export interface ServiceModel extends JsonLike {
	/** The ID of the entity */
	id?: number;
}
